// EmailWebinar.js
// Vonzás 02 · Email Varázsló · 4. kártya — Webinár szekvencia.
//
// 4 mód (props.mode): promo · reminder · followup · full
//   Mód-specifikus inputok (props): webinar_idopont, webinar_cim,
//   webinar_fo_pontok, fo_felismeresek, kovetkezo_lepes, deadline,
//   stilus_pill, stilus_pill_promo, stilus_pill_followup.
//
// Pipeline:
//   POST /attraction/email/webinar/generate → 5/6/5/16 e-mail
//   POST /attraction/email/webinar/refine   → snippet vagy full rewrite
//   POST /attraction/email/webinar/save     → Tudástár
//
// RENDER: Welcome-stílusú 2-paneles shell (lila refine panel bal · cream
// doc jobb · top controls). Mindegyik e-mail: cím (= Email neve) + kék
// timing pille (= küldési idő) + tárgysor pillek + fehér body card.
// Backend-only meta (Fázis, Stratégiai szerep, Használt szög, Célja,
// Preview text) NEM jelenik meg.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  CircleNotch, Copy as CopyIcon,
  DownloadSimple, CheckCircle, Paperclip, PaperPlaneTilt,
  X as XIcon,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";
import { getActiveAlap } from "../lib/activeAlap";

const ACCENT_BLUE = "#26A9C9";
const ACCENT_BLUE_DARK = "#135b6c";
const ACCENT_BLUE_TINT_PILL = "rgba(38, 169, 201, 0.14)";
const ACCENT_BLUE_TINT_BLOCK = "rgba(38, 169, 201, 0.10)";
const ACCENT_BLUE_BORDER_BLOCK = "rgba(38, 169, 201, 0.30)";
const SUBJECT_PILL_BG = "rgba(38, 169, 201, 0.08)";
const SUBJECT_PILL_BORDER = "rgba(38, 169, 201, 0.35)";
const TIMING_PILL_BG = "rgba(38, 169, 201, 0.10)";
const TIMING_PILL_BORDER = "rgba(38, 169, 201, 0.32)";

const QUICK_REFINE_ACTIONS = [
  { id: "edge",     label: "Élesebb",    instruction: "Erősebb érzelmi feszültség, mélyebb fájdalom, konkrétabb jelenet, nagyobb hatás." },
  { id: "soft",     label: "Lágyabb",    instruction: "Melegebb, empatikusabb, barátságosabb, több megértés." },
  { id: "concrete", label: "Konkrétabb", instruction: "Több konkrét részlet, időpont, hely, tárgy, érzés - semmi általánosítás." },
  { id: "story",    label: "Több sztori", instruction: "Cseréld ki vagy bővítsd ki egy rövid, életszagú személyes vagy hipotetikus jelenettel." },
  { id: "humor",    label: "Humorosabb", instruction: "Adj bele finom öniróniát vagy könnyed humort, de maradj empatikus." },
  { id: "surprise", label: "Lepj meg!",  instruction: "Legyél kreatívabb, váratlanabb, erősebb hook vagy fordulat ebben a részben." },
];

const PLAIN_BRACKETS = new Set([
  "keresztnév", "keresztneved",
  "név", "neved",
  "a te neved", "a neved", "coachod neve", "coach neve",
  "aláírás", "signature",
]);

// Frontend safety net: a Writer/parser elvileg már strippelte a markdown
// jelölőket (`**`, `***`, `*`, `---`, `##`), de ha valami átszivárogna a body
// vagy a timing mezőkbe, itt is leszedjük renderelés előtt.
function stripStrayMd(s) {
  if (!s) return s;
  return String(s)
    .replace(/\*{1,3}/g, "")
    .replace(/^\s*#{1,6}\s*/gm, "")
    .replace(/^\s*(?:---+|\*\*\*+|===+|___+)\s*$/gm, "");
}

// Technikai jelölés (D-7, -5 nap, D+1) → emberi magyar formát ad. Védőháló a
// mentett, régi vault entry-kre. (Az új generálások már a backend parser-ben
// kapnak humanizálást, ez itt megegyező logika frontend-szinten.)
function humanizeKuldesiIdo(s) {
  if (!s || typeof s !== "string") return s || "";
  let t = s.trim();
  if (!t) return "";
  // Ha már emberi formátum, hagyjuk.
  if (/^Ma,\s+a\s+webinár\s+napján/i.test(t)) return t;
  if (/^Webinár\s+(előtt|után)\s+\d+\s+nappal/i.test(t)) return t;
  // "webinár napja / a webinár napján" → "Ma, a webinár napján"
  const napjaRe = /(?:\ba\s+)?webin[áa]ri?\s+(?:napj[aoá]n?|napon)[a-záéíóöőúüű]*/i;
  const mNapja = t.match(napjaRe);
  if (mNapja) {
    return (t.slice(0, mNapja.index) + "Ma, a webinár napján" + t.slice(mNapja.index + mNapja[0].length)).trim();
  }
  // D-N vagy -N nap / -N nappal előtte
  let m = t.match(/(?:D\s*[-−–]\s*|(?:^|(?<=\s))[-−–]\s*)(\d+)\s*(?:nap(?:pal)?)?(?:\s*el[őo]tte?)?/i);
  if (m) {
    const n = parseInt(m[1], 10);
    const rep = n === 0 ? "Ma, a webinár napján"
              : n === 1 ? "Webinár előtt 1 nappal"
              : `Webinár előtt ${n} nappal`;
    return (t.slice(0, m.index) + rep + t.slice(m.index + m[0].length)).trim().replace(/^,\s*/, "").trim();
  }
  // D+N vagy +N nap / +N nappal után
  m = t.match(/(?:D\s*\+\s*|(?:^|(?<=\s))\+\s*)(\d+)\s*(?:nap(?:pal)?)?(?:\s*ut[áa]n)?/i);
  if (m) {
    const n = parseInt(m[1], 10);
    const rep = n === 0 ? "Ma, a webinár napján"
              : n === 1 ? "Webinár után 1 nappal"
              : `Webinár után ${n} nappal`;
    return (t.slice(0, m.index) + rep + t.slice(m.index + m[0].length)).trim().replace(/^,\s*/, "").trim();
  }
  // 'D0' szóló jelölés
  if (/^D\s*[+\-−–]?\s*0$/i.test(t)) return "Ma, a webinár napján";
  return t;
}

// REMINDER mode only: a küldési-idő pill KIZÁRÓLAG a kiküldési időzítést
// mutassa. Ha a Writer véletlenül belefűzött lane / role tokent (pl.
// "Registration Confirmation", "Live Stake / FOMO", "E4", "Confirmation"),
// stripleljük — timing marad, minden más kikerül. Promo/Followup változatlan.
function stripReminderRoleLabels(s) {
  if (!s || typeof s !== "string") return s || "";
  const rolePatterns = [
    /\b(Registration Confirmation|Clarity\s*\/\s*Value Preview|Live Stake\s*\/\s*FOMO|Show-?Up Identity|One Hour\s*\/\s*Action)\b/gi,
    /\b(Confirmation|Live Stake|Identity Shift|Show-?Up|Invitation)\b/gi,
    /\bE[1-5]\b/g,
    /\b(strategic_lane|email[_ ]role|lane|role)\s*[:=]\s*[^,·|—–\-]+/gi,
  ];
  let t = s;
  for (const re of rolePatterns) t = t.replace(re, "");
  // Clean up leftover separators / whitespace
  t = t.replace(/^\s*[·|—–\-,:]+\s*/g, "")
       .replace(/\s*[·|—–\-,:]+\s*$/g, "")
       .replace(/\s*[·|—–\-,:]\s*[·|—–\-,:]+\s*/g, " · ")
       .replace(/\s{2,}/g, " ")
       .trim();
  return t;
}

export default function EmailWebinar({
  initialContent = null,
  initialRawText = "",
  mode = "promo",
  inputs = {},
}) {
  const [content, setContent] = useState(initialContent);
  const [rawText, setRawText] = useState(initialRawText);
  const [chosenSubjects, setChosenSubjects] = useState(() => {
    const d = {};
    (initialContent?.emails || []).forEach((e) => { d[e.email_szam] = 0; });
    return d;
  });

  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingVault, setSavingVault] = useState(false);

  const [selection, setSelection] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [refinePhase, setRefinePhase] = useState("idle");
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const undoTimerRef = useRef(null);
  const previewRef = useRef(null);

  const activeAlap = useMemo(() => getActiveAlap() || {}, []);

  // Composed full text for clipboard + refine context.
  const composedAllText = useMemo(() => {
    if (!content?.emails?.length) return rawText || "";
    return rawText || "";
  }, [content, rawText]);

  // Selection capture
  useEffect(() => {
    const handleMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (text.length < 3) return;
      const root = previewRef.current;
      if (!root) return;
      let node = sel.anchorNode;
      while (node && node !== root) node = node.parentNode;
      if (!node) return;
      setSelection(text.slice(0, 1200));
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, []);

  const clearSelection = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const refinePayload = (extras = {}) => ({
    voice_id: activeAlap.voice,
    client_id: activeAlap.client,
    offer_id: activeAlap.offer,
    mode,
    jelenlegi_sorozat: composedAllText,
    webinar_idopont: inputs.webinar_idopont || "",
    webinar_cim: inputs.webinar_cim || "",
    webinar_fo_pontok: inputs.webinar_fo_pontok || "",
    fo_felismeresek: inputs.fo_felismeresek || "",
    kovetkezo_lepes: inputs.kovetkezo_lepes || "",
    deadline: inputs.deadline || "",
    stilus_pill: inputs.stilus_pill || "",
    stilus_pill_promo: inputs.stilus_pill_promo || "",
    stilus_pill_followup: inputs.stilus_pill_followup || "",
    ...extras,
  });

  const requestRefine = async (instructionOverride, quickButtonId = null) => {
    const utasitas = (instructionOverride || chatInput).trim();
    if (!utasitas && !quickButtonId) return;
    if (refinePhase === "loading" || refinePhase === "suggestion") return;
    const quoteToSend = selection;
    setSuggestionError(false);
    setRefinePhase("loading");
    if (!instructionOverride) setChatInput("");
    try {
      const res = await api.post("/attraction/email/webinar/refine", refinePayload({
        utasitas: utasitas || "",
        quote: quoteToSend || null,
        quick_button: quickButtonId || null,
      }));
      const data = res.data || {};
      const m = data?.metadata?.mode === "snippet" ? "snippet" : "teljes";
      setSuggestion({
        mod: m,
        snippet: data.snippet || "",
        teljes: data.teljes_szoveg || "",
        content: data.webinar_content || null,
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
      });
      setRefinePhase("suggestion");
    } catch {
      setSuggestion({
        mod: "error",
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
      });
      setSuggestionError(true);
      setRefinePhase("suggestion");
    }
  };

  const retryVariant = async () => {
    if (!suggestion || regenerating) return;
    const prev = suggestion.mod === "snippet" ? (suggestion.snippet || "") : (suggestion.teljes || "");
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const res = await api.post("/attraction/email/webinar/refine", refinePayload({
        utasitas: (suggestion.instr || "") +
          "\n\nGenerálj egy érdemben MÁSIK változatot, mint az előzőt." +
          (prev ? ` Az előző javaslat eleje így szólt: "${prev.slice(0, 280)}". Kerüld el az ismétlést.` : ""),
        quote: suggestion.quote || null,
      }));
      const data = res.data || {};
      const m = data?.metadata?.mode === "snippet" ? "snippet" : "teljes";
      setSuggestion((s) => ({ ...s, mod: m, snippet: data.snippet || "", teljes: data.teljes_szoveg || "", content: data.webinar_content || null }));
    } catch { setSuggestionError(true); }
    finally { setRegenerating(false); }
  };

  const retryAfterError = () => {
    if (!suggestion) return;
    setSuggestionError(false);
    requestRefine(suggestion.instr);
  };

  const cancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const prevText = composedAllText;
    let nextText = prevText;
    if (suggestion.mod === "snippet" && suggestion.snippet && suggestion.quote) {
      const idx = prevText.indexOf(suggestion.quote);
      if (idx >= 0) {
        nextText = prevText.slice(0, idx) + suggestion.snippet + prevText.slice(idx + suggestion.quote.length);
      } else {
        setRefinePhase("idle"); setSuggestion(null); return;
      }
    } else if (suggestion.mod === "teljes" && suggestion.teljes) {
      nextText = suggestion.teljes;
    } else {
      setRefinePhase("idle"); setSuggestion(null); return;
    }
    setUndoSnapshot({ content, rawText, chosenSubjects });
    setRawText(nextText);
    if (suggestion.mod === "snippet") {
      if (content && Array.isArray(content.emails)) {
        const nextEmails = content.emails.map((em) => {
          const old = em.body || "";
          if (old.includes(suggestion.quote)) {
            return { ...em, body: old.replace(suggestion.quote, suggestion.snippet) };
          }
          return em;
        });
        setContent({ ...content, emails: nextEmails });
      }
    } else {
      const nc = suggestion.content || null;
      const ne = Array.isArray(nc?.emails) ? nc.emails : [];
      if (ne.length > 0) {
        setContent(nc);
        const next = {};
        ne.forEach((e) => { next[e.email_szam] = chosenSubjects[e.email_szam] ?? 0; });
        setChosenSubjects(next);
      } else {
        setContent(null);
      }
    }
    setSelection(null);
    setSaved(false);
    window.getSelection()?.removeAllRanges();
    setTimeout(() => { try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ } }, 0);
    setSuggestion(null);
    setRefinePhase("applied");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRefinePhase("idle"); setUndoSnapshot(null);
    }, 8000);
  };

  const undoApplied = () => {
    if (!undoSnapshot) return;
    setContent(undoSnapshot.content);
    setRawText(undoSnapshot.rawText);
    setChosenSubjects(undoSnapshot.chosenSubjects);
    setUndoSnapshot(null);
    setRefinePhase("idle");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  useEffect(() => () => { if (undoTimerRef.current) clearTimeout(undoTimerRef.current); }, []);

  const handleSubmitChat = () => requestRefine();

  // Cream actions
  const handleCopy = async () => {
    if (!composedAllText) return;
    try {
      await navigator.clipboard.writeText(composedAllText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Vágólapra másolva");
    } catch { toast.error("Másolás nem sikerült"); }
  };
  const handleCopyOne = async (em) => {
    if (!em || !em.body) return;
    try {
      await navigator.clipboard.writeText(em.body);
      toast.success("Levél másolva");
    } catch { toast.error("Másolás nem sikerült"); }
  };
  const handleDownload = () => {
    if (!composedAllText) return;
    const blob = new Blob([composedAllText], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "webinar-szekvencia.txt";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };
  const handleSave = async () => {
    if (!composedAllText || saved || savingVault) return;
    setSavingVault(true);
    const firstEmail = content?.emails?.[0];
    const firstSubj = (firstEmail?.targysorok || [])[chosenSubjects[firstEmail?.email_szam] ?? 0]
      || (firstEmail?.targysorok || [])[0]
      || firstEmail?.nev
      || "";
    const saveName = (firstSubj || "").trim();
    try {
      await api.post("/attraction/email/webinar/save", {
        name: saveName || null,
        teljes_szoveg: composedAllText,
        webinar_content: content || null,
        chosen_subjects: chosenSubjects,
        mode,
        inputs,
      });
      setSaved(true);
      toast.success("Mentve a Tudástárba");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mentés sikertelen.");
    } finally {
      setSavingVault(false);
    }
  };

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  const isLoading = refinePhase === "loading";
  const showSuggestion = refinePhase === "suggestion" && suggestion;
  const showApplied = refinePhase === "applied";
  const inputDisabled = isLoading || showSuggestion;

  const emails = content?.emails || [];
  const hasStructured = emails.length > 0;
  const placeholder = selection ? "Mit változtassak a kijelölt részen?" : "Mit szeretnél finomítani?";
  const suggestionSnippet = suggestion
    ? (suggestion.mod === "snippet" ? (suggestion.snippet || "") : (suggestion.teljes || ""))
    : "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="flex flex-col h-full"
      style={{ background: "#0a0a12" }}
      data-testid="email-webinar-shell"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">
        {/* LEFT — Finomító Panel */}
        <div className="flex flex-col border-r min-h-0 h-full"
             style={{ borderColor: "rgba(255,255,255,0.04)" }}
             data-testid="email-webinar-finomito">
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div className="font-heading"
                   style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.3, letterSpacing: "-0.015em", color: "#fff", marginBottom: 18 }}>
                Elkészült a szekvencia.
              </div>
              <div style={{ fontSize: 17, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0" }}>
                Olvasd át jobboldalt. Ha minden rendben, mentsd a Tudástárba. Ha valamin finomítanál, jelölj ki egy részt, vagy írd lent, mit változtassak.
              </div>
            </div>

            <AnimatePresence>
              {(selection || isLoading || showSuggestion) && (
                <motion.div key="ws" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                  style={{ background: "rgba(124,108,242,0.025)", border: "1px solid rgba(124,108,242,0.10)", borderRadius: 12, padding: 14, margin: "32px -8px 0" }}
                  data-testid="email-webinar-selection-workspace">
                  {(selection || (suggestion && suggestion.quote)) && (
                    <div style={{ background: "rgba(255,255,255,0.04)", borderLeft: "2px solid rgba(124,108,242,0.40)", borderRadius: 8, padding: "11px 13px", marginBottom: 12 }}>
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em", color: "#8a8a9c", fontWeight: 600, marginBottom: 4 }}>KIJELÖLT RÉSZ</div>
                          <div style={{ fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {(() => { const q = selection || suggestion?.quote || ""; return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`; })()}
                          </div>
                        </div>
                        {!showSuggestion && !isLoading && (
                          <button type="button" onClick={clearSelection} aria-label="Kijelölés törlése"
                            style={{ width: 22, height: 22, borderRadius: 5, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#9898ad", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
                            data-testid="email-webinar-selection-clear"><XIcon size={11} weight="bold" /></button>
                        )}
                      </div>
                    </div>
                  )}

                  {!isLoading && !showSuggestion && selection && (
                    <div className="grid grid-cols-2 gap-2.5" data-testid="email-webinar-quick-actions">
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button key={qa.id} type="button" onClick={() => requestRefine(qa.instruction, qa.id)} data-testid={`email-webinar-quick-${qa.id}`}
                          style={{ fontSize: 13, padding: "10px 14px", borderRadius: 999, background: "rgba(124,108,242,0.10)", border: "1px solid rgba(124,108,242,0.28)", color: "#cfc8ff", cursor: "pointer", fontFamily: "inherit", fontWeight: 500, whiteSpace: "nowrap", textAlign: "center", width: "100%" }}>{qa.label}</button>
                      ))}
                    </div>
                  )}

                  {isLoading && (
                    <div className="flex items-center gap-2.5" style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }} data-testid="email-webinar-refine-loading">
                      <CircleNotch size={14} weight="bold" className="animate-spin" /> Finomítás folyamatban…
                    </div>
                  )}

                  {showSuggestion && (
                    <div data-testid="email-webinar-suggestion">
                      <SuggestionCard
                        snippet={suggestionSnippet}
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={applySuggestion}
                        onRetryVariant={retryVariant}
                        onRetryAfterError={retryAfterError}
                        onCancel={cancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showApplied && (
                <motion.div key="app" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }} style={{ margin: "24px 0 0" }} data-testid="email-webinar-applied">
                  <AppliedBlock onUndo={undoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); if (chatInput.trim() && !inputDisabled) handleSubmitChat(); }} style={{ padding: "12px 24px 24px" }} data-testid="email-webinar-finomito-form">
            <div style={{ background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)", border: "1px solid rgba(243,238,228,0.14)", borderRadius: 12, padding: "5px 5px 5px 14px", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)", opacity: inputDisabled ? 0.5 : 1, pointerEvents: inputDisabled ? "none" : "auto" }}>
              <button type="button" aria-label="Csatolás" style={{ background: "transparent", border: "none", padding: 0, color: "#8a8a9c", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <Paperclip size={14} weight="regular" />
              </button>
              <input value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); if (chatInput.trim() && !inputDisabled) handleSubmitChat(); } }}
                placeholder={placeholder} data-testid="email-webinar-chat-input" disabled={inputDisabled}
                className="flex-1 outline-none"
                style={{ background: "transparent", border: "none", color: "white", fontSize: 14, padding: "8px 0", fontFamily: "inherit" }} />
              <button type="submit" disabled={!chatInput.trim() || inputDisabled} data-testid="email-webinar-chat-send" aria-label="Küldés"
                style={{ width: 36, height: 36, background: "linear-gradient(135deg, #a89bff, #7c6cf2)", color: "white", borderRadius: 9, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed", opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5, boxShadow: chatInput.trim() && !inputDisabled ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)" : "none" }}>
                {isLoading ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : <PaperPlaneTilt size={15} weight="fill" />}
              </button>
            </div>
            <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
              <span>Tipp: jelölj ki egy részt jobboldalt - a kérésed arra fog vonatkozni.</span>
              <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
            </div>
          </form>
        </div>

        {/* RIGHT — krém dokumentum */}
        <div className="overflow-y-auto h-full" style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}>
          <div ref={previewRef} data-preview-theme="cream" data-testid="email-webinar-cream-card" className="mr-auto cream-selectable"
            style={{
              background: "#f3eee4", color: "#0a0a12", borderRadius: 10,
              boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
              padding: "32px 44px 56px", maxWidth: 860, marginLeft: 0, position: "relative",
              fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
            }}>
            <div className="flex justify-end items-center" style={{ gap: 6, marginBottom: 28, position: "relative", zIndex: 1 }}>
              <CreamActionPill onClick={handleCopy} icon={copied ? CheckCircle : CopyIcon} label={copied ? "Másolva" : "Másolás"} testid="email-webinar-copy" />
              <CreamActionPill onClick={handleDownload} icon={DownloadSimple} label="Letöltés" testid="email-webinar-download" />
              <CreamActionPill onClick={handleSave} icon={saved ? CheckCircle : DownloadSimple} label={savingVault ? "Mentés…" : saved ? "Mentve" : "Mentés a Tudástárba"} testid="email-webinar-save" active={saved} disabled={savingVault || saved} />
            </div>

            {hasStructured ? (
              emails.map((em, i) => {
                const subjects = em.targysorok || [];
                const idx = chosenSubjects[em.email_szam] ?? 0;
                return (
                  <WebinarEmailBlock
                    key={em.email_szam || i}
                    email={em}
                    nev={em.nev}
                    chosenIdx={idx}
                    onChoose={(ix) => setChosenSubjects((p) => ({ ...p, [em.email_szam]: ix }))}
                    onCopyOne={() => handleCopyOne(em)}
                    isFirst={i === 0}
                    isLast={i === emails.length - 1}
                    subjects={subjects}
                    mode={mode}
                  />
                );
              })
            ) : (
              <pre style={{ fontFamily: "inherit", fontSize: 15, lineHeight: 1.75, whiteSpace: "pre-wrap", color: "#2a2a35" }}>{rawText || ""}</pre>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ────────────────────── WEBINAR EMAIL BLOCK ────────────────────── */

function WebinarEmailBlock({ email, nev, chosenIdx, onChoose, onCopyOne, isFirst, isLast, subjects, mode }) {
  const { email_szam, kuldesi_ido = "", body = "" } = email;
  const subEyebrowStyle = {
    fontSize: 13, color: ACCENT_BLUE, fontWeight: 600,
    letterSpacing: "0.14em", textTransform: "uppercase",
    marginBottom: 12,
  };
  // Header: "Email N · [rövid cím]" - tisztán, NO N/összes fraction, NO
  // separate strategic badge line. A `nev` a Writer "## Email #N - [Rövid név]"
  // header-jéből jön. A `##` és bármely markdown-maradék itt is leszedjük.
  const cleanNev = stripStrayMd((nev || "").replace(/^#+\s*/, ""));
  // Reminder módban a pill CSAK a kiküldési időzítést mutassa — lane / role
  // tokenek (Confirmation / Live Stake / E1 stb.) kiszűrve.
  const rawTiming = stripStrayMd(kuldesi_ido).trim();
  const preClean = mode === "reminder" ? stripReminderRoleLabels(rawTiming) : rawTiming;
  const cleanTiming = humanizeKuldesiIdo(preClean);
  const cleanBody = stripStrayMd(body);
  const headerText = cleanNev
    ? `Email #${email_szam} · ${cleanNev}`
    : `Email #${email_szam}`;

  return (
    <div data-testid={`email-webinar-block-${email_szam}`} style={{ marginTop: isFirst ? 84 : 0, marginBottom: 0 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 14, marginBottom: 24, flexWrap: "wrap",
      }}>
        <div
          data-testid={`email-webinar-${email_szam}-title`}
          style={{ fontSize: 21, fontWeight: 600, color: "#0a0a12", letterSpacing: "-0.01em", lineHeight: 1.25 }}
        >
          {headerText}
        </div>
        {cleanTiming && (
          <span
            data-testid={`email-webinar-${email_szam}-timing`}
            style={{
              fontSize: 11.5, padding: "5px 12px", borderRadius: 999,
              background: ACCENT_BLUE, border: `1px solid ${ACCENT_BLUE}`,
              color: "#ffffff", fontWeight: 500,
              letterSpacing: "0.02em", whiteSpace: "nowrap",
            }}
          >{cleanTiming}</span>
        )}
      </div>

      {subjects && subjects.length > 0 && (
        <div style={{ marginBottom: 26 }}>
          <div style={subEyebrowStyle}>Tárgysor-opciók</div>
          <div className="flex flex-wrap gap-2">
            {subjects.map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onChoose(i)}
                data-testid={`email-webinar-${email_szam}-subject-${i}`}
                data-selected={i === chosenIdx ? "true" : "false"}
                style={{
                  fontSize: 14, fontWeight: 400, padding: "8px 16px", borderRadius: 999,
                  background: SUBJECT_PILL_BG, border: `1px solid ${SUBJECT_PILL_BORDER}`,
                  color: "#2a2a35", cursor: "pointer", fontFamily: "inherit",
                  transition: "background 0.15s, border 0.15s",
                }}
              >{stripStrayMd(t)}</button>
            ))}
          </div>
        </div>
      )}

      <div style={subEyebrowStyle}>Szövegtörzs</div>
      <div data-testid={`email-webinar-${email_szam}-body-card`}
        style={{
          position: "relative", background: "#ffffff",
          border: "1px solid rgba(20,30,40,0.06)", borderRadius: 10,
          padding: "22px 26px 44px 26px",
          boxShadow: "0 1px 0 rgba(255,255,255,0.40) inset, 0 6px 18px -10px rgba(0,0,0,0.10)",
        }}>
        <BodyWithPlaceholders body={cleanBody} />
        <button type="button" onClick={onCopyOne} data-testid={`email-webinar-${email_szam}-copy-one`} aria-label="Levél másolása" title="Levél másolása"
          style={{
            position: "absolute", right: 10, bottom: 10, width: 30, height: 30, borderRadius: 8,
            background: "transparent", border: "1px solid rgba(20,30,40,0.10)",
            color: ACCENT_BLUE_DARK, cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            transition: "background 180ms ease, border 180ms ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = ACCENT_BLUE_TINT_BLOCK; e.currentTarget.style.border = `1px solid ${ACCENT_BLUE_BORDER_BLOCK}`; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.border = "1px solid rgba(20,30,40,0.10)"; }}>
          <CopyIcon size={14} weight="regular" />
        </button>
      </div>

      {!isLast && (
        <div style={{ height: 0, borderBottom: "1px solid rgba(0,0,0,0.12)", marginTop: 52, marginBottom: 52 }} />
      )}
    </div>
  );
}

function BodyWithPlaceholders({ body }) {
  if (!body) return null;
  const parts = [];
  const regex = /\[([^\]\n]{1,160})\]/g;
  let lastIdx = 0;
  let m;
  let keyCounter = 0;
  while ((m = regex.exec(body)) !== null) {
    if (m.index > lastIdx) parts.push({ type: "text", text: body.slice(lastIdx, m.index), key: keyCounter++ });
    const inner = m[1].trim();
    const isPlain = PLAIN_BRACKETS.has(inner.toLowerCase());
    parts.push({ type: isPlain ? "plain-bracket" : "chip", text: inner, key: keyCounter++ });
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < body.length) parts.push({ type: "text", text: body.slice(lastIdx), key: keyCounter++ });
  return (
    <div style={{ fontSize: 15, color: "#2a2a35", lineHeight: 1.75, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
      {parts.map((p) => {
        if (p.type === "text") return <React.Fragment key={p.key}>{p.text}</React.Fragment>;
        if (p.type === "plain-bracket") return <React.Fragment key={p.key}>[{p.text}]</React.Fragment>;
        return <PlaceholderChip key={p.key} text={p.text} />;
      })}
    </div>
  );
}

function PlaceholderChip({ text }) {
  const [value, setValue] = useState(text);
  const ref = useRef(null);
  return (
    <span ref={ref} contentEditable suppressContentEditableWarning spellCheck={false}
      onBlur={(e) => setValue(e.currentTarget.innerText.trim())}
      data-testid="email-placeholder-chip" data-original={text}
      style={{
        display: "inline-block", padding: "0 4px", margin: "0 1px",
        borderRadius: 4, background: "rgba(38, 169, 201, 0.06)",
        boxShadow: "inset 0 -1px 0 rgba(38, 169, 201, 0.22)",
        color: "#1f3a45", fontWeight: 500, fontSize: "0.97em",
        lineHeight: "inherit", cursor: "text", outline: "none",
      }}>{value}</span>
  );
}

function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled }) {
  const accentBg = active ? "rgba(29,158,117,0.10)" : ACCENT_BLUE_TINT_PILL;
  const accentBorder = active ? "rgba(29,158,117,0.30)" : "rgba(38, 169, 201,0.35)";
  const accentText = active ? "#1d9e75" : ACCENT_BLUE_DARK;
  return (
    <button type="button" onClick={disabled ? undefined : onClick} data-testid={testid} disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px", background: accentBg,
        border: `1px solid ${accentBorder}`, borderRadius: 999,
        color: accentText, fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        whiteSpace: "nowrap", fontFamily: "inherit", transition: "all 180ms ease",
      }}>
      <Icon size={13} weight="regular" /> {label}
    </button>
  );
}
